import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Plus, Trash2, Target, Pencil } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Goals({ user, onLogout }) {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    target_amount: '',
    current_amount: '',
    deadline: ''
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoals(response.data);
    } catch (error) {
      toast.error('Failed to fetch goals');
    }
  };

  const handleAddNew = () => {
    setEditMode(false);
    setEditingId(null);
    setFormData({
      title: '',
      target_amount: '',
      current_amount: '',
      deadline: ''
    });
    setOpen(true);
  };

  const handleEdit = (goal) => {
    setEditMode(true);
    setEditingId(goal.id);
    setFormData({
      title: goal.title,
      target_amount: goal.target_amount.toString(),
      current_amount: goal.current_amount.toString(),
      deadline: goal.deadline.split('T')[0] // Format date for input
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const goalData = {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        current_amount: parseFloat(formData.current_amount),
        deadline: new Date(formData.deadline).toISOString()
      };

      if (editMode) {
        await axios.put(
          `${API}/goals/${editingId}`,
          goalData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Goal updated successfully!');
      } else {
        await axios.post(
          `${API}/goals`,
          goalData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Goal created successfully!');
      }

      setOpen(false);
      setFormData({ title: '', target_amount: '', current_amount: '', deadline: '' });
      setEditMode(false);
      setEditingId(null);
      fetchGoals();
    } catch (error) {
      toast.error(editMode ? 'Failed to update goal' : 'Failed to create goal');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/goals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Goal deleted!');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900" data-testid="goals-page">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/goals" />

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2" data-testid="goals-title">
              Financial Goals
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Track progress towards your savings goals</p>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={handleAddNew}
                className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white rounded-full"
                data-testid="add-goal-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-gray-800" data-testid="goal-dialog">
              <DialogHeader>
                <DialogTitle className="dark:text-gray-100">
                  {editMode ? 'Edit Goal' : 'Create New Goal'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="dark:text-gray-300">Goal Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Emergency Fund, Vacation"
                    required
                    data-testid="title-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Target Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.target_amount}
                    onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="target-amount-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Current Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.current_amount}
                    onChange={(e) => setFormData({ ...formData, current_amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="current-amount-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Deadline</Label>
                  <Input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    required
                    data-testid="deadline-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="submit-goal"
                >
                  {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Goal' : 'Create Goal')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="goals-grid">
          {goals.length > 0 ? (
            goals.map((goal) => {
              const progress = (goal.current_amount / goal.target_amount) * 100;
              return (
                <Card key={goal.id} className="glass p-6 card-hover dark:bg-gray-800" data-testid={`goal-item-${goal.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(goal)}
                        className="text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                        data-testid={`edit-goal-${goal.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(goal.id)}
                        className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                        data-testid={`delete-goal-${goal.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">{goal.title}</h3>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-400">Progress</span>
                      <span className="font-semibold text-purple-600 dark:text-purple-400">{progress.toFixed(0)}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Current:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{formatCurrency(goal.current_amount, user?.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Target:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{formatCurrency(goal.target_amount, user?.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Deadline:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{new Date(goal.deadline).toLocaleDateString()}</span>
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12" data-testid="no-goals">
              <p className="text-gray-500 dark:text-gray-400">No goals yet. Create your first goal!</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}